import { useCallback, useRef, useState } from 'react'
import { ACCEPTED_UPLOAD_TYPES, MAX_UPLOAD_BYTES } from '@budgetmonkey/shared'
import { Button } from '@/components/ui'
import { formatBytes } from '@/lib/format'
import type { UploadItem } from './hooks'

/**
 * Desktop upload — REQUIREMENTS.md §6.3.
 *
 * Drag-drop and file picker, multiple files at once. Each file reports its own
 * progress and failure: dropping eight receipts and losing the batch because
 * one was a screenshot would be worse than useless.
 */
export function ReceiptDropZone({
  items,
  onUpload,
  onDismiss,
  onClearFinished,
}: {
  items: readonly UploadItem[]
  onUpload: (files: readonly File[]) => void
  onDismiss: (id: string) => void
  onClearFinished: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  // Nested elements fire dragleave on every boundary crossing; counting
  // enter/leave pairs is what keeps the highlight from flickering.
  const dragDepth = useRef(0)

  const handleFiles = useCallback(
    (list: FileList | null) => {
      if (list === null || list.length === 0) return
      onUpload([...list])
    },
    [onUpload],
  )

  const active = items.filter((i) => i.status === 'uploading')
  const finished = items.filter((i) => i.status !== 'uploading')

  return (
    <div className="space-y-4">
      <div
        onDragEnter={(e) => {
          e.preventDefault()
          dragDepth.current += 1
          setDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          dragDepth.current -= 1
          if (dragDepth.current <= 0) setDragging(false)
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          dragDepth.current = 0
          setDragging(false)
          handleFiles(e.dataTransfer.files)
        }}
        className={`rounded-xl border-2 border-dashed px-6 py-10 text-center transition ${
          dragging
            ? 'border-slate-900 bg-slate-100'
            : 'border-slate-300 bg-white hover:border-slate-400'
        }`}
      >
        <p className="font-medium text-slate-900">
          {dragging ? 'Drop to upload' : 'Drag receipts here'}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          Photos or PDFs, up to {MAX_UPLOAD_BYTES / 1024 / 1024} MB each. Several at
          once is fine.
        </p>

        <Button
          type="button"
          variant="primary"
          className="mt-4"
          onClick={() => inputRef.current?.click()}
        >
          Choose files
        </Button>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_UPLOAD_TYPES.join(',')}
          className="sr-only"
          onChange={(e) => {
            handleFiles(e.target.files)
            // Reset so re-picking the same file fires change again.
            e.target.value = ''
          }}
        />
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-600" role="status" aria-live="polite">
              {active.length > 0
                ? `Uploading ${active.length} file${active.length === 1 ? '' : 's'}…`
                : `${finished.filter((i) => i.status === 'done').length} uploaded`}
            </p>
            {finished.length > 0 && active.length === 0 && (
              <Button variant="ghost" onClick={onClearFinished}>
                Clear
              </Button>
            )}
          </div>

          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {items.map((item) => (
              <li key={item.id} className="px-4 py-3">
                <UploadRow item={item} onDismiss={() => onDismiss(item.id)} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function UploadRow({ item, onDismiss }: { item: UploadItem; onDismiss: () => void }) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
          {item.fileName}
        </span>
        <span className="text-xs text-slate-500">{formatBytes(item.sizeBytes)}</span>
        {item.status === 'uploading' && (
          <span className="text-xs text-slate-500">
            {Math.round(item.progress * 100)}%
          </span>
        )}
        {item.status === 'done' && (
          <span className="text-xs font-medium text-emerald-700">Uploaded</span>
        )}
        {item.status === 'error' && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-slate-500 underline underline-offset-4"
          >
            Dismiss
          </button>
        )}
      </div>

      {item.status === 'uploading' && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full bg-slate-800 transition-[width]"
            style={{ width: `${Math.round(item.progress * 100)}%` }}
          />
        </div>
      )}

      {item.status === 'error' && (
        <p role="alert" className="text-xs text-red-700">
          {item.error}
        </p>
      )}
    </div>
  )
}
